import { useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { apiUrl } from './apiUrl'
import { STAFF_ROLE_CLAIM } from './role'

const ROLE_CLAIM = 'https://structuralclimbing.com/role'
const GYM_ID_CLAIM = 'https://structuralclimbing.com/gym_id'

// Fingerprint of everything the sync writes, remembered per user in localStorage.
// Once the profiles row matches the JWT there is nothing to write, so on every
// later mount we skip the upsert + backfill entirely — previously they fired on
// each mount for every user, forever, pure overhead once the row was correct.
// Any change to email/role/gym/staff-role/name changes the fingerprint and
// re-triggers the sync (e.g. a promotion or a gym switch).
const SIGNATURE_KEY_PREFIX = 'sc:profile-sync:'

function readSignature(userId: string): string | null {
  try {
    return localStorage.getItem(SIGNATURE_KEY_PREFIX + userId)
  } catch {
    return null // storage unavailable (private mode / disabled) — sync every mount
  }
}

function writeSignature(userId: string, signature: string) {
  try {
    localStorage.setItem(SIGNATURE_KEY_PREFIX + userId, signature)
  } catch {
    // storage unavailable — nothing cached, so the next mount just syncs again
  }
}

/**
 * Keeps the user's profiles row in step with Auth0, through the Worker's
 * POST /sync-profile. The Worker takes role, gym and staff role from the access
 * token and email and name from the ID token sent here, so nothing in the
 * request body can change them. The values below only build the fingerprint.
 */
export function useProfileSync() {
  const { isAuthenticated, user, getAccessTokenSilently, getIdTokenClaims } = useAuth0()

  useEffect(() => {
    if (!isAuthenticated || !user?.sub) return
    const role = user[ROLE_CLAIM] as string | undefined
    const gymId = user[GYM_ID_CLAIM] as string | undefined

    if (!role) return

    const staffRole = (user[STAFF_ROLE_CLAIM] as string | undefined) ?? null

    const firstName = user.given_name as string | undefined
    const lastName = user.family_name as string | undefined
    const userId = user.sub
    const email = user.email!

    // Order-fixed fingerprint of the synced fields. Matches what we last wrote?
    // Then the row is already correct — nothing to do.
    const signature = JSON.stringify([
      email,
      role,
      gymId ?? null,
      staffRole,
      firstName ?? null,
      lastName ?? null,
    ])
    if (readSignature(userId) === signature) return

    let cancelled = false

    // With no access token there is nothing to send: the Worker would only
    // answer 401, and the real failure is already in the auth telemetry. The
    // fingerprint stays unwritten, so the next mount tries again.
    const sync = async () => {
      let token: string
      try {
        token = await getAccessTokenSilently()
      } catch {
        return
      }
      if (cancelled) return
      try {
        const idToken = (await getIdTokenClaims())?.__raw
        if (!idToken || cancelled) return
        const res = await fetch(`${apiUrl()}/sync-profile`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_token: idToken }),
        })
        if (!res.ok) {
          console.error('Profile sync error:', res.status)
          return
        }
        // Remember the fingerprint only after a clean sync, so a failed write
        // retries on the next mount instead of being cached away.
        writeSignature(userId, signature)
      } catch (e) {
        // Offline or aborted. The next mount syncs again; the fingerprint was
        // never written.
        console.error('Profile sync failed:', e)
      }
    }
    void sync()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, user, getAccessTokenSilently, getIdTokenClaims])
}
